import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Briefcase, Clock, Bell, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const Calendario = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadEventos();
  }, [currentDate]);

  const loadEventos = async () => {
    try {
      const [audienciasRes, processosRes, anotacoesRes] = await Promise.all([
        api.getAudiencias(),
        api.getProcessos(),
        api.getAnotacoes()
      ]);

      const eventosFormatados = [];

      // Audiências
      audienciasRes.data.forEach(aud => {
        if (aud.data_audiencia) {
          eventosFormatados.push({
            id: aud.id,
            tipo: 'audiencia',
            titulo: `Audiência: ${aud.tipo_audiencia}`,
            subtitulo: aud.processo?.numero_processo || '',
            data: new Date(aud.data_audiencia),
            cor: 'bg-purple-500',
            corClara: 'bg-purple-100 text-purple-700 border-purple-300'
          });
        }
      });

      // Prazos de processos
      processosRes.data.forEach(proc => {
        if (proc.data_limite) {
          eventosFormatados.push({
            id: proc.id,
            tipo: 'prazo',
            titulo: `Prazo: ${proc.numero_processo}`,
            subtitulo: proc.cliente?.nome_completo || '',
            data: new Date(proc.data_limite),
            cor: 'bg-red-500',
            corClara: 'bg-red-100 text-red-700 border-red-300'
          });
        }
      });

      // Anotações
      anotacoesRes.data.forEach(anot => {
        if (anot.data_alerta && !anot.concluida) {
          eventosFormatados.push({
            id: anot.id,
            tipo: 'anotacao',
            titulo: anot.titulo,
            subtitulo: anot.descricao || '',
            data: new Date(anot.data_alerta),
            cor: 'bg-blue-500',
            corClara: 'bg-blue-100 text-blue-700 border-blue-300'
          });
        }
      });

      setEventos(eventosFormatados);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  // Funções do calendário
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Dias do mês anterior
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }
    
    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Dias do próximo mês
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  const getEventosForDate = (date) => {
    return eventos.filter(evento => {
      const eventoDate = new Date(evento.data);
      return eventoDate.toDateString() === date.toDateString();
    });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const days = getDaysInMonth(currentDate);
  const selectedDateEventos = selectedDate ? getEventosForDate(selectedDate) : [];

  // Próximos eventos (7 dias)
  const hoje = new Date();
  const em7Dias = new Date(hoje.getTime() + 7 * 24 * 60 * 60 * 1000);
  const proximosEventos = eventos
    .filter(e => e.data >= hoje && e.data <= em7Dias)
    .sort((a, b) => a.data - b.data)
    .slice(0, 5);

  if (loading) {
    return <div className="p-8" data-testid="calendario-loading">Carregando...</div>;
  }

  return (
    <div className="p-8 space-y-6" data-testid="calendario-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Calendário</h1>
          <p className="text-slate-600 mt-1">Visualize audiências, prazos e anotações</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={goToToday}>
            Hoje
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendário Principal */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={prevMonth}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-xl">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Cabeçalho dos dias */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="text-center text-sm font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Dias do mês */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dayEventos = getEventosForDate(day.date);
                const hasEventos = dayEventos.length > 0;
                
                return (
                  <div
                    key={index}
                    onClick={() => setSelectedDate(day.date)}
                    className={`
                      min-h-[80px] p-1 border rounded-lg cursor-pointer transition-all
                      ${!day.isCurrentMonth ? 'bg-slate-50 text-slate-400' : 'bg-white'}
                      ${isToday(day.date) ? 'border-blue-500 border-2' : 'border-slate-200'}
                      ${isSelected(day.date) ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
                      hover:bg-slate-50
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${isToday(day.date) ? 'text-blue-600' : ''}`}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayEventos.slice(0, 3).map((evento, idx) => (
                        <div
                          key={idx}
                          className={`text-xs px-1 py-0.5 rounded truncate ${evento.cor} text-white`}
                          title={evento.titulo}
                        >
                          {evento.titulo.substring(0, 12)}...
                        </div>
                      ))}
                      {dayEventos.length > 3 && (
                        <div className="text-xs text-slate-500 px-1">
                          +{dayEventos.length - 3} mais
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legenda */}
            <div className="flex gap-4 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-purple-500"></div>
                <span className="text-sm text-slate-600">Audiências</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-500"></div>
                <span className="text-sm text-slate-600">Prazos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-blue-500"></div>
                <span className="text-sm text-slate-600">Anotações</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Eventos */}
        <div className="space-y-6">
          {/* Eventos do dia selecionado */}
          {selectedDate && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  {selectedDate.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDateEventos.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDateEventos.map((evento, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg border ${evento.corClara}`}
                      >
                        <div className="flex items-start gap-2">
                          {evento.tipo === 'audiencia' && <Briefcase className="w-4 h-4 mt-0.5" />}
                          {evento.tipo === 'prazo' && <AlertTriangle className="w-4 h-4 mt-0.5" />}
                          {evento.tipo === 'anotacao' && <Bell className="w-4 h-4 mt-0.5" />}
                          <div>
                            <p className="font-medium text-sm">{evento.titulo}</p>
                            {evento.subtitulo && (
                              <p className="text-xs opacity-75">{evento.subtitulo}</p>
                            )}
                            <p className="text-xs mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {evento.data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Nenhum evento neste dia
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Próximos eventos */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Próximos 7 dias</CardTitle>
            </CardHeader>
            <CardContent>
              {proximosEventos.length > 0 ? (
                <div className="space-y-2">
                  {proximosEventos.map((evento, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded-lg border ${evento.corClara} cursor-pointer`}
                      onClick={() => setSelectedDate(evento.data)}
                    >
                      <p className="font-medium text-sm truncate">{evento.titulo}</p>
                      <p className="text-xs opacity-75">
                        {evento.data.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })}
                        {' - '}
                        {evento.data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  Nenhum evento próximo
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Calendario;
